import React, { useContext, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { HomeOutlined, SettingOutlined } from "@ant-design/icons";

import { homePath, settingsPath, userPublicationsPath } from "../../../routes/routes";

import "./styles/NewNB.css";
import "./styles/NewNavBar.css";

import { AutoCompleteSearch } from "../../../components/dataInput/AutoCompleteSearch";
import { NotificationsBadgesBlog } from "../../../components/notifications/NotificationsBadgesBlog";
import { LogoNomada } from "../../../components/uiElements/LogoNomada";
import { useMediaQuery } from "react-responsive";
import { UserDataContext } from "../../../contexts/Contexts";
import { useProfileImage } from "../../../../infrastructure/hooks/useProfileImagen";
import { requestHomeFeedStart } from "../../home/homeFeedNavigation";

const FEED_ALIGN_SELECTOR =
  ".home-content, .other-user-content, .new-up-content";

export const NewNavBar = ({
  showDesktopLogo = false,
  showProfileAvatar = false,
  profileAvatarAction = "profile",
}) => {
  const { userData } = useContext(UserDataContext);
  const isMobile = useMediaQuery({ maxWidth: 1200 });
  const { pathname } = useLocation();
  const showSettingsShortcut = !pathname.startsWith(settingsPath);
  const userType = Number(userData?.idTipoUsuario);
  const avatarTypeClass =
    {
      1: "new-nb-profile-avatar--unverified",
      2: "new-nb-profile-avatar--verified",
      3: "new-nb-profile-avatar--business",
      4: "new-nb-profile-avatar--broker",
    }[userType] || "new-nb-profile-avatar--unverified";

  const [searchActive, setSearchActive] = useState(false);
  const navRef = useRef(null);
  const searchTrackRef = useRef(null);
  const { profileSrc: profileAvatar } = useProfileImage(userData);

  useLayoutEffect(() => {
    if (isMobile) return undefined;

    const nav = navRef.current;
    const track = searchTrackRef.current;
    if (!nav || !track) return undefined;

    let rafId = 0;
    let observedFeed = null;
    let lastOffset = null;

    const applyAlignment = (aligned, offsetPx = null) => {
      nav.classList.toggle("new-nb-home--feed-aligned", aligned);

      if (!aligned) {
        nav.style.removeProperty("--vn-search-align-left");
        lastOffset = null;
        return;
      }

      if (lastOffset !== offsetPx) {
        nav.style.setProperty("--vn-search-align-left", `${offsetPx}px`);
        lastOffset = offsetPx;
      }
    };

    const syncSearchToFeed = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const feed = document.querySelector(FEED_ALIGN_SELECTOR);
        if (!feed) {
          applyAlignment(false);
          return;
        }

        // Apply layout class first so track metrics match final styles.
        if (!nav.classList.contains("new-nb-home--feed-aligned")) {
          nav.classList.add("new-nb-home--feed-aligned");
        }

        const feedLeft = feed.getBoundingClientRect().left;
        const trackLeft = track.getBoundingClientRect().left;
        const offset = Math.max(0, Math.round(feedLeft - trackLeft));
        applyAlignment(true, offset);
      });
    };

    const resizeObserver = new ResizeObserver(syncSearchToFeed);

    const attachFeedObserver = () => {
      const feed = document.querySelector(FEED_ALIGN_SELECTOR);
      if (feed === observedFeed) {
        syncSearchToFeed();
        return;
      }
      if (observedFeed) resizeObserver.unobserve(observedFeed);
      observedFeed = feed;
      if (feed) resizeObserver.observe(feed);
      syncSearchToFeed();
    };

    resizeObserver.observe(document.documentElement);
    resizeObserver.observe(nav);
    attachFeedObserver();

    window.addEventListener("resize", syncSearchToFeed);

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      window.removeEventListener("resize", syncSearchToFeed);
      nav.classList.remove("new-nb-home--feed-aligned");
      nav.style.removeProperty("--vn-search-align-left");
    };
  }, [isMobile, pathname]);

  const renderProfileAvatar = () =>
    !showProfileAvatar ? null : profileAvatarAction === "menu" ? (
      <button
        type="button"
        className="new-nb-profile-avatar-link new-nb-profile-avatar-button"
        aria-label="Abrir menu"
        onClick={() => {
          window.dispatchEvent(new CustomEvent("vn-sidebar-menu:open"));
        }}
      >
        <img
          src={profileAvatar}
          alt=""
          className={`new-nb-profile-avatar ${avatarTypeClass}`}
          onError={(event) => {
            event.currentTarget.onerror = null;
            event.currentTarget.src = "/img/icons/UserFalse.svg";
          }}
        />
      </button>
    ) : (
      <Link
        to={userPublicationsPath}
        state={userData}
        className="new-nb-profile-avatar-link"
        aria-label="Ir a mi perfil"
      >
        <img
          src={profileAvatar}
          alt="profile"
          className={`new-nb-profile-avatar ${avatarTypeClass}`}
          onError={(event) => {
            event.currentTarget.onerror = null;
            event.currentTarget.src = "/img/icons/UserFalse.svg";
          }}
        />
      </Link>
    );

  return (
    <>
      {!isMobile && (
        <div
          ref={navRef}
          className="nb-search-container new-nb-home"
        >
          {/* El avatar solo se pinta aqui cuando la pantalla lo pide (hoy solo
              Mensajeria, que sustituye el menu lateral por la lista de
              conversaciones y se queda sin el lugar donde vive normalmente).
              Esta columna esta clavada al ancho del menu lateral, y ese ancho
              alcanza justo para el logo: al meterle ademas el avatar el contenido
              ya no cabe y el centrado empuja al avatar fuera del borde izquierdo.
              El modificador deja que la columna crezca solo en ese caso. */}
          <div
            className={`new-nb-logo-container ${
              showProfileAvatar ? "new-nb-logo-container--with-avatar" : ""
            }`}
          >
            {renderProfileAvatar()}
            <div
              className={`new-nb-logo new-nb-logo--desktop-visible`}
            >
               <Link to={homePath} onClick={requestHomeFeedStart}>
                <LogoNomada logoColor={2} className="logo-container" />
              </Link> 
            </div>
          </div>
          <div className="new-nb-search-icons-container" ref={searchTrackRef}>
            <div className="new-nb-search-container">
              <div className="new-nb-search">
                <AutoCompleteSearch />
              </div>
            </div>

            <div className="new-nb-icons-container">
              {/* El cambio de idioma salio de aqui: ahora es un apartado propio
                  en Configuracion, debajo de Configuracion y privacidad. */}

              {/* Acceso directo al inicio. Reinicia el feed igual que el logo. */}
              <div>
                <Link
                  to={homePath}
                  onClick={requestHomeFeedStart}
                  className="nb-home-link"
                  aria-label="Ir al inicio"
                >
                  <HomeOutlined className="nb-home-icon" />
                </Link>
              </div>

              <div>
                <NotificationsBadgesBlog />
              </div>

              {showSettingsShortcut ? (
              <div>
                <Link
                  to={settingsPath}
                  className="nb-settings-link"
                  aria-label="Configuración"
                >
                  <SettingOutlined className="nb-settings-icon nb-settings-icon--gear" />
                </Link>
              </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

   
        {isMobile && (
        <div className={`nbm-wrap ${searchActive ? "nb--search-active" : ""}`}>
       
          <div className="nbm-left">{renderProfileAvatar()}</div>
    
          <div className="nbm-logo">
            <Link to={homePath} onClick={requestHomeFeedStart}>
              <LogoNomada logoColor={2} className="logo-container" />
            </Link>
          </div>
  
          <div className="nbm-search">
            <AutoCompleteSearch onActiveChange={setSearchActive} />
          </div>
        </div>
      )}
    </>
  );
};

export default NewNavBar;
